import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { isItemRedacted } from '@/utilities/redaction.utilities';
import { itemIconClass } from '@/utilities/utilities';
import { Icon } from '@seeqdev/qomponents';
import { UnitOfMeasure } from '@/core/UnitOfMeasure.atom';
import type { TrendSignal } from '@/plugin/pluginApiDefinition';
import _ from 'lodash';
import { AddToDisplayPane } from '@/workbook/AddToDisplayPane.molecule';
import { setInvestigateItem } from '@/utilities/detailsPanel.utilities';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { setAutoFocusToolsTab } from '@/main/layout.actions';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { addTrendItem } from '@/trendData/trend.actions';
import { HoverTooltip } from '@/core/HoverTooltip.atom';

interface TrendSignalParameter extends TrendSignal {
  ancestors?: { id: string; name: string }[];
}

interface SummaryReportItemViewProps {
  item?: TrendSignalParameter;
  showAncestors?: boolean;
  showUnitOfMeasure?: boolean;
  dataTestId?: string;
  isPresentation?: boolean;
}

export const SummaryReportItemView: React.FunctionComponent<SummaryReportItemViewProps> = ({
  item,
  showAncestors = false,
  showUnitOfMeasure = false,
  dataTestId = 'specSelectedItem',
  isPresentation = false,
}) => {
  const { t } = useTranslation();

  const handleItemClick = () => {
    if (!item || isPresentation) {
      return;
    }
    // Must be in the details pane to be visible in the properties panel
    const trendItems = getAllItems({});
    if (!_.some(trendItems, { id: item.id })) {
      void addTrendItem(item);
    }
    setInvestigateItem(item.id, TREND_TOOLS.PROPERTIES);
    setAutoFocusToolsTab(true);
  };

  const displayItem = () => {
    if (!item) {
      return null;
    }

    const unitOfMeasure = { value: item.valueUnitOfMeasure, fromSourceUom: false, isDisplay: false };
    const iconClassName = clsx('pr7', 'fa', 'sq-tool-description-color', itemIconClass(item));
    const defaultTextColor = 'rgb(var(--tw-color-sq-text-color))';

    if (isItemRedacted(item)) {
      return (
        <div className="flexColumnContainer pt8">
          <Icon icon="fa-warning" type="danger" extraClassNames="pr5" />
          <div className="flexRowContainer flexFill pb1">
            <div className="flexColumnContainer font-size-smaller">{t('NO_ITEM_ACCESS')}</div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flexColumnContainer flexCenter">
          <Icon icon={iconClassName} type="color" color={item.color || defaultTextColor} />
          <div
            id={dataTestId}
            className={clsx('flexRowContainer flexFill pb1 selectItem_itemName aggressiveWordBreak')}>
            <div>
              <HoverTooltip text="INVESTIGATE_TOOLS.SUMMARY_REPORT.ITEM_PROPERTIES_TOOLTIP" placement="right">
                <strong
                  className={clsx('mr5', {
                    'force-link-look cursorPointer': !isPresentation,
                  })}
                  onClick={handleItemClick}>
                  {item.name}
                  {showUnitOfMeasure && ' '}
                </strong>
              </HoverTooltip>
              {showUnitOfMeasure && <UnitOfMeasure unitOfMeasure={unitOfMeasure} />}
            </div>
            {showAncestors && (
              <div className="xsmall itemAsset text-italic">{_.join(_.map(item.ancestors, 'name'), ' » ')}</div>
            )}
          </div>
          {!isPresentation && <AddToDisplayPane itemId={item.id} />}
        </div>
      );
    }
  };

  return <div>{displayItem()}</div>;
};
